import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PlaceIcon from '@mui/icons-material/Place';
import { useTranslation } from 'react-i18next';
import { forwardGeocode, reverseGeocode, type GeocodeResult } from '../utils/geocode';
import { MapContainer, Marker, TileLayer, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import markerIconUrl from 'leaflet/dist/images/marker-icon.png';
import markerIcon2xUrl from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadowUrl from 'leaflet/dist/images/marker-shadow.png';

// Fix the default marker icon — Leaflet's CSS references images that
// won't resolve under Vite's module bundler. Import the PNGs from the
// package so Vite serves them same-origin — keeps the strict CSP
// img-src 'self' viable and drops the unpkg CDN dependency (#347).
const defaultIcon = L.icon({
  iconUrl: markerIconUrl,
  iconRetinaUrl: markerIcon2xUrl,
  shadowUrl: markerShadowUrl,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = defaultIcon;

interface MapPickerProps {
  /** Initial latitude (Augsburg if null on first mount). */
  lat: number | null;
  /** Initial longitude. */
  lng: number | null;
  /** Initial address shown in the search field (e.g. on edit). */
  address?: string;
  onChange: (next: { lat: number; lng: number; address: string }) => void;
  disabled?: boolean;
  /** Search input placeholder. */
  searchLabel?: string;
  /**
   * Reports whether the search field holds text that was not searched yet or
   * whose result was not picked. The form blocks saving while this is true
   * (#547), because an address without a position is the wrong-house bug.
   */
  onPendingChange?: (pending: boolean) => void;
}

const AUGSBURG: [number, number] = [48.3705, 10.8978];

const MapPicker: React.FC<MapPickerProps> = ({
  lat,
  lng,
  address,
  onChange,
  disabled = false,
  searchLabel = 'Adresse suchen',
  onPendingChange,
}) => {
  const { t } = useTranslation('content');
  const [query, setQuery] = useState(address ?? '');
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<GeocodeResult[]>([]);
  const [pin, setPin] = useState<[number, number] | null>(
    lat !== null && lng !== null ? [lat, lng] : null,
  );
  const skipExternalSync = useRef(false);

  // Reflect external lat/lng changes (e.g. on form load).
  useEffect(() => {
    if (skipExternalSync.current) {
      skipExternalSync.current = false;
      return;
    }
    if (lat !== null && lng !== null) setPin([lat, lng]);
  }, [lat, lng]);

  useEffect(() => {
    if (address !== undefined) setQuery(address);
  }, [address]);

  // Text in the field that is not the confirmed address is pending.
  const pending = query.trim() !== '' && query.trim() !== (address ?? '').trim();
  useEffect(() => {
    onPendingChange?.(pending);
  }, [pending, onPendingChange]);

  const pickResult = useCallback(
    (result: GeocodeResult) => {
      skipExternalSync.current = true;
      setResults([]);
      setPin([result.lat, result.lng]);
      setQuery(result.displayName);
      onChange({ lat: result.lat, lng: result.lng, address: result.displayName });
    },
    [onChange],
  );

  const handleSearch = useCallback(async () => {
    if (disabled || !query.trim()) return;
    setSearching(true);
    setError(null);
    setResults([]);
    try {
      const bias = pin ? { lat: pin[0], lng: pin[1] } : { lat: AUGSBURG[0], lng: AUGSBURG[1] };
      const found = await forwardGeocode(query, bias);
      if (found.length === 0) {
        setError(t('station.location.noResults'));
        return;
      }
      if (found.length === 1) {
        pickResult(found[0]);
        return;
      }
      setResults(found);
    } catch {
      setError(t('station.location.failed'));
    } finally {
      setSearching(false);
    }
  }, [query, disabled, pin, pickResult, t]);

  const handlePinMove = useCallback(
    async (nextLat: number, nextLng: number) => {
      skipExternalSync.current = true;
      setPin([nextLat, nextLng]);
      setResults([]);
      setError(null);
      // Best-effort reverse geocode; failure leaves the search field as-is.
      try {
        const display = await reverseGeocode(nextLat, nextLng);
        if (display) {
          setQuery(display);
          onChange({ lat: nextLat, lng: nextLng, address: display });
          return;
        }
      } catch {
        /* ignore */
      }
      onChange({ lat: nextLat, lng: nextLng, address: query });
    },
    [onChange, query],
  );

  const initialCentre = useMemo<[number, number]>(
    () => pin ?? AUGSBURG,
    [pin],
  );

  return (
    <Stack spacing={1}>
      <TextField
        fullWidth
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            void handleSearch();
          }
        }}
        placeholder={searchLabel}
        size="small"
        disabled={disabled}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={searchLabel}
                  onClick={() => void handleSearch()}
                  disabled={disabled || searching || !query.trim()}
                  edge="end"
                >
                  {searching ? <CircularProgress size={16} /> : <SearchIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          },
        }}
      />

      {error && <Alert severity="warning">{error}</Alert>}

      {results.length > 1 && (
        <Paper variant="outlined">
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', px: 2, pt: 1 }}>
            {t('station.location.results')}
          </Typography>
          <List dense aria-label={t('station.location.results')}>
            {results.map((result) => (
              <ListItemButton key={`${result.lat},${result.lng}`} onClick={() => pickResult(result)} disabled={disabled}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <PlaceIcon fontSize="small" color="action" />
                </ListItemIcon>
                <ListItemText primary={result.displayName} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      <Box
        sx={{
          height: 280,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
        }}
      >
        <MapContainer
          center={initialCentre}
          zoom={pin ? 15 : 13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom={!disabled}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecentreOnPin pin={pin} />
          <ClickHandler onPick={(p) => void handlePinMove(p.lat, p.lng)} disabled={disabled} />
          {pin && (
            <Marker
              position={pin}
              draggable={!disabled}
              eventHandlers={{
                dragend: (e) => {
                  const m = e.target as L.Marker;
                  const ll = m.getLatLng();
                  void handlePinMove(ll.lat, ll.lng);
                },
              }}
            />
          )}
        </MapContainer>
      </Box>

      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
        {pin && (
          <>
            <MyLocationIcon fontSize="small" color="action" />
            <Typography variant="caption" color="text.secondary">
              {pin[0].toFixed(5)}, {pin[1].toFixed(5)}
            </Typography>
          </>
        )}
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
          {t('station.location.attribution')}
        </Typography>
      </Stack>
    </Stack>
  );
};

const RecentreOnPin: React.FC<{ pin: [number, number] | null }> = ({ pin }) => {
  const map = useMap();
  useEffect(() => {
    if (pin) map.flyTo(pin, Math.max(map.getZoom(), 14), { duration: 0.5 });
  }, [pin, map]);
  return null;
};

const ClickHandler: React.FC<{
  onPick: (p: { lat: number; lng: number }) => void;
  disabled: boolean;
}> = ({ onPick, disabled }) => {
  useMapEvents({
    click(e) {
      if (disabled) return;
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
};

export default MapPicker;
